import { autoinject } from 'aurelia-framework';
import { microTask } from 'app/shared/lib/decorators';
import { getAttributeBinding } from 'app/shared/lib/binding';
import { changeTag } from 'app/shared/lib/dom';
import { CustomElementBase } from 'app/shared/elements/custom-element-base';
import { guid } from 'app/shared/lib/guid';

export const TAB_ATTRIBUTE_MAP = new Map<string, string>([
    ['active', 'ess-active-tab']
]);

export function isTabActive(tab: Element): boolean {
    return Array.from(tab.attributes).some(({ name }) => /^active/.test(name)) || tab.classList.contains('ess-active');
}

function toggleTab(tab: Element, isToggled?: boolean) {
    // if tab's role is 'menuitem', tab needs 'aria-current' attribute; otherwise, 'aria-selected' for tabs with 'tab' role
    const ariaAttribute: string = tab.getAttribute('role') === 'menuitem' ? 'aria-current' : 'aria-selected';
    if(isToggled === undefined) isToggled = tab.getAttribute(ariaAttribute) === 'false';
    tab.setAttribute('active', `${isToggled}`);
    tab.setAttribute(ariaAttribute, `${isToggled}`);
    tab.setAttribute('tabindex', `${isToggled ? 0 : -1}`);
    tab.classList.toggle('ess-active', isToggled);
}

function toggleTabPanel(panel: Element, isToggled?: boolean) {
    if(isToggled === undefined) {
        isToggled = panel.getAttribute('aria-expanded') === 'false';
    }
    panel.setAttribute('aria-expanded', `${isToggled}`);
    panel.toggleAttribute('hidden', !isToggled);
    panel.setAttribute('tabindex', '-1');
}

export function getTabWidth(tab: Element) {
    const { marginLeft, marginRight } = getComputedStyle(tab);
    return tab.clientWidth + parseInt(marginLeft) + parseInt(marginRight);
}

export function getTabPanelForTab(tab: Element) {
    const activatedTabPanelId = tab.getAttribute('aria-controls');
    return activatedTabPanelId ? document.getElementById(activatedTabPanelId) : null;
}

export enum TabClass {
    Tab = 'ess-tab',
    GroupingTab = 'ess-grouping-tab',
    GroupedTab = 'ess-grouped-tab',
    TabGroupDropdown = 'ess-tab-group-dropdown',
    // PinnedTab = 'ess-tab-pinned', // to be uncommented when horizontal tabs implements this base
    Focused = 'ess-focused', // general
    TabPanel = 'ess-tabpanel',
    Processing = 'ess-processing' // general
}

export const TabSelector = {
    Tab: `.${TabClass.Tab}:not(.${TabClass.GroupingTab})`,
    AllTab: `.${TabClass.Tab}`,
    GroupingTab: `.${TabClass.GroupingTab}`,
    TabGroupDropdown: `.${TabClass.TabGroupDropdown}`,
    GroupedTab: `.${TabClass.GroupedTab}`,
    TabById: (id: string) => `[aria-controls="${id}"]`,
    // PinnedTab: `.${TabClass.PinnedTab}`, // to be uncommented when horizontal tabs implements this base
    TabPanel: `.${TabClass.TabPanel}`
}

export interface ITabs {
    activateTab(elementOrId: Element | string): void;
}

export interface ITabChangedEventDetails {
    readonly tabs: ITabs;
    readonly activeIndex: number;
    readonly activeTab: Element;
}

export type TabChangedEvent = CustomEvent<ITabChangedEventDetails>;

@autoinject()
export abstract class TabsBase extends CustomElementBase implements ITabs {

    ////****************** Fields and Properties ************************************////
    protected id = guid();

    protected activeTab: HTMLElement | null = null;
    protected focusedTab: HTMLElement | null = null;
    protected tabs!: HTMLElement[];
    protected tabWidths: Map<HTMLElement, number> = new Map<HTMLElement, number>();

    protected endGroup: { tab: HTMLElement, content: string }[] = [];
    protected startGroup: { tab: HTMLElement, content: string }[] = [];
    protected groupDropdownOpened: boolean = false;

    protected tabObserver: MutationObserver = new MutationObserver(() => this.recomputeTabs());
    protected resizeObserver: ResizeObserver = new ResizeObserver(() => this.recomputeTabs());

    ////****************** Element References ***************************************////
    protected $tablist!: HTMLElement;
    protected $leftGroupingTab!: HTMLElement;
    protected $rightGroupingTab!: HTMLElement;
    protected $leftTabGroupDropdown!: HTMLElement;
    protected $rightTabGroupDropdown!: HTMLElement;
    protected $leftTabGroup!: HTMLElement;
    protected $rightTabGroup!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    protected abstract attached(): void;
    protected abstract detached(): void;

    ////****************** Events ***************************************************////
    protected handleTabClick(event: MouseEvent): boolean | void {
        const tab = (event.target as HTMLElement).closest<HTMLElement>(TabSelector.AllTab); // don't exclude grouping tabs
        if(tab === null) return false;
        this.activateTab(tab);
        return getTabPanelForTab(tab) === null;
    }

    protected handleFocusin({ target, relatedTarget }: FocusEvent): void {
        const previous = relatedTarget as Node | null;
        if(!this.element.contains(previous) ||
            (this.$tablist.contains(target as Node | null) && !this.$tablist.contains(previous))) {
            if(this.groupDropdownOpened) this.toggleGroupDropdown();
            this.updateFocus(this.activeTab);
        }
    }

    protected handleFocusout({ target, relatedTarget }: FocusEvent): void {
        if(!this.element.contains(relatedTarget as Node | null)) {
            if(this.groupDropdownOpened) this.toggleGroupDropdown();
            this.updateFocus(null);
            if(this.activeTab) this.activeTab.setAttribute('tabindex', '0');
        }
    }

    protected abstract handleKeydown(event: KeyboardEvent): true | void;

    ////****************** Functions ************************************************////
    protected toggleGroupDropdown(groupingTab?: HTMLElement | null): void {
        this.groupDropdownOpened = groupingTab ? !this.groupDropdownOpened : false;
        this.element.querySelectorAll(TabSelector.GroupingTab).forEach((tab) => {
            tab.setAttribute('aria-expanded', `${tab === groupingTab}`);
        });
        this.element.querySelectorAll(TabSelector.TabGroupDropdown).forEach((dropdown) => {
            dropdown.classList.toggle('ess-closed', dropdown.previousElementSibling !== groupingTab);
        });
    }

    @microTask
    public activateTab(activatedTab: Element | string | null): void {
        // if activated tab is a grouping tab, open the appropriate dropdown; do not update this.activeTab
        if(activatedTab === this.$leftGroupingTab || activatedTab === this.$rightGroupingTab) {
            this.updateFocus(<HTMLElement>activatedTab);
            return this.toggleGroupDropdown(<HTMLElement>activatedTab);
        }

        if(typeof (activatedTab) === 'string') {
            activatedTab = this.element.querySelector(TabSelector.TabById(activatedTab));
        }
        const activatedTabPanel = activatedTab === null ? null : getTabPanelForTab(activatedTab);

        let tabPanels = Array.from(this.element.querySelectorAll(TabSelector.TabPanel));

        Array.from(this.element.querySelectorAll(TabSelector.Tab))
            .sort((a, b) => Number(a === activatedTab) - Number(b === activatedTab)) // Toggle the active tab(s) last
            .forEach(tab => {
                toggleTab(tab, tab === activatedTab);

                const panel = getTabPanelForTab(tab);
                if(panel && tabPanels.indexOf(panel) === -1) { tabPanels.push(panel); }
            });
        tabPanels.forEach(panel => toggleTabPanel(panel, panel === activatedTabPanel));

        this.activeTab = activatedTab as HTMLElement;
    }

    protected updateFocus(focusedTab: HTMLElement | null, tabGroup: HTMLElement | null = null): void {
        this.focusedTab = focusedTab;
        this.$tablist.querySelectorAll(TabSelector.AllTab).forEach((tab) => {
            tab.classList.toggle(TabClass.Focused, tab === focusedTab);
            tab.setAttribute('tabindex', tab === focusedTab ? '0' : '-1');
        });
        if(!tabGroup) {
            if(this.groupDropdownOpened) this.toggleGroupDropdown();
            return;
        }
        tabGroup.querySelectorAll(TabSelector.GroupedTab).forEach((tab) => {
            tab.classList.toggle(TabClass.Focused, tab === focusedTab);
            tab.setAttribute('tabindex', tab === focusedTab ? '0' : '-1');
        });
    }

    protected abstract recomputeTabs(): void;

    protected abstract consolidateTabs(): void;

    protected static processContent(_compiler: any, _resources: any, el: Element, _instruction: any): true {
        // for each <ess-tab>
        const tabs = Array.from(el.querySelectorAll(TabClass.Tab));
        tabs.forEach((tab) => {

            const givenPanelId = !!getAttributeBinding(tab, 'tab-for');
            const hasRepeater = tab.hasAttribute('repeat.for');
            let tabId = getAttributeBinding(tab, 'id') ?? guid();
            let panelId = getAttributeBinding(tab, 'tab-for') || `panel-${tabId}`;

            // if tab is a repeater, append the ID pairs with the tab's index
            if(hasRepeater) {
                tabId += '-${$index}';
                panelId += '-${$index}';
            }

            // transform the header
            let tabHeader = tab.querySelector('ess-tab-header') || tab.querySelector('[slot="header"]')
            if(tabHeader === null) {
                tabHeader = document.createElement('div');
            } else {
                if(/ess-tab-header/i.test(tabHeader.tagName)) {
                    tabHeader = changeTag(tabHeader, 'div');
                }
                if(Array.from(tabHeader.attributes).some(({ name, value }) => /^header/.test(name))) {
                    // TODO [Matt] Static, so we can't reference this.logger. Fix.
                    console.warn('A tab header element or slot may be overwritten by a header attribute or binding.', tabHeader);
                }
                tabHeader.remove();
            }

            // normalize the header
            Array.from(tab.attributes).forEach(({ name, value }) => {
                if(/^header/.test(name)) {
                    tabHeader!.innerHTML = name === 'header' ? value : `\${${value}}`;
                }
                else {
                    Array.from(TAB_ATTRIBUTE_MAP).some(([match, newName]) => {
                        const doesMatch = name.startsWith(match);
                        if(doesMatch) {
                            name = name.replace(match, newName);
                        }
                        return doesMatch;
                    });
                    tabHeader!.setAttribute(name, value);
                }
            });

            if(!tabHeader.getAttribute('id')) { tabHeader.setAttribute('id', tabId); }
            tabHeader.setAttribute('slot', 'header');
            tabHeader.setAttribute('role', 'tab');
            tabHeader.setAttribute('aria-controls', panelId);
            tabHeader.classList.add(TabClass.Tab);
            el.append(tabHeader);

            // transform the body/panel
            if(!givenPanelId && tab.innerHTML.trim()) {
                const tabBody = document.createElement('div');
                tabBody.setAttribute('id', panelId);
                tabBody.setAttribute('role', 'tabpanel');
                tabBody.setAttribute('aria-labelledby', tabId);
                tabBody.classList.add(TabClass.TabPanel);
                tabBody.innerHTML = tab.innerHTML;
                if(hasRepeater) tabBody.setAttribute('repeat.for', tab.getAttribute('repeat.for')!);
                el.append(tabBody);
            }

            tab.remove();
        });
        return true;
    }
}
